/**
 * Two-section asset selector for the Asset Positions tab — an "Available
 * Assets" 4-up grid on top and a "Selected Assets" chip row underneath with
 * an "N of 4 selected" counter.
 *
 * Visual language matches `mars-forecasts/AssetSelector.tsx` 1:1 (same outer
 * card, header tone and chip styling) — keeping the two surfaces identical
 * was an explicit user request.
 */
import { ASSET_COLORS_WITH_ALIASES as ASSET_COLORS } from '@/lib/plotly/colors';
import { MAX_ASSET_SELECTIONS } from '../store';

interface ExposuresAssetChooserProps {
  assetClasses: string[];
  selected: string[];
  onToggle: (asset: string) => void;
}

/** API asset-class keys -> display labels. */
function displayLabel(key: string): string {
  if (key === 'Bonds') return 'Fixed Income';
  if (key === 'Inflation') return 'Inflation Swaps';
  return key;
}

export default function ExposuresAssetChooser({
  assetClasses,
  selected,
  onToggle,
}: ExposuresAssetChooserProps): JSX.Element {
  const labels = assetClasses.map(displayLabel);
  const selectionCount = selected.length;

  return (
    <div
      className="rounded-md border border-[#e2e8f0] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.05)] mb-3 overflow-hidden"
      style={{ fontFamily: "'Gill Sans MT', 'Gill Sans', 'Trebuchet MS', sans-serif" }}
    >
      {/* 12px outer padding and gaps; a tighter 6px compresses the controls
       * cluster and shifts the chart up. */}
      <div className="p-3">
        <div className="mb-2 text-[0.875rem] font-medium text-[#374151]">Available Assets</div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2">
          {labels.map((label) => {
            const isSelected = selected.includes(label);
            const disabled = !isSelected && selectionCount >= MAX_ASSET_SELECTIONS;
            return (
              <AvailableAssetBox
                key={label}
                label={label}
                selected={isSelected}
                disabled={disabled}
                onClick={() => onToggle(label)}
              />
            );
          })}
        </div>

        <div className="mt-3 rounded-md border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="shrink-0 text-[0.875rem] font-medium text-[#374151]">
              Selected Assets
            </span>
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
              {selected.length === 0 ? (
                <span className="text-[0.75rem] italic text-[#9ca3af]">No assets selected</span>
              ) : (
                selected.map((label) => (
                  <SelectedChip key={label} label={label} onRemove={() => onToggle(label)} />
                ))
              )}
            </div>
            <span className="shrink-0 rounded-md border border-[#e2e8f0] bg-white px-2 py-0.5 text-[0.7rem] text-[#6b7280]">
              {selectionCount} of {MAX_ASSET_SELECTIONS}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AvailableAssetBoxProps {
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}

/** One clickable box in the "Available Assets" grid (~33px tall). */
function AvailableAssetBox({
  label,
  selected,
  disabled,
  onClick,
}: AvailableAssetBoxProps): JSX.Element {
  const swatch = ASSET_COLORS[label] ?? '#666';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'flex items-center justify-between rounded-md border px-2 py-1.5',
        'text-[0.75rem] font-medium cursor-pointer select-none',
        'transition-colors',
        selected ? 'bg-[#f0fdfa] border-[#4EB7AC]' : 'bg-white border-[#e5e7eb]',
        !selected && !disabled ? 'hover:bg-[#f0fdfa] hover:border-[#4EB7AC]' : '',
        disabled ? 'opacity-50 cursor-not-allowed bg-[#f9fafb]' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden
          className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm border border-black/10"
          style={{ backgroundColor: swatch }}
        />
        <span className="text-[#374151]">{label}</span>
      </span>
      <span className="text-xs">
        {selected ? (
          <span className="font-bold text-[#4EB7AC]">&#10003;</span>
        ) : (
          <span className="text-[#6b7280]">+</span>
        )}
      </span>
    </button>
  );
}

interface SelectedChipProps {
  label: string;
  onRemove: () => void;
}

/** One removable chip in the "Selected Assets" row. */
function SelectedChip({ label, onRemove }: SelectedChipProps): JSX.Element {
  const swatch = ASSET_COLORS[label] ?? '#666';
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-[#4EB7AC] bg-white px-1.5 py-0.5 text-[0.75rem] font-medium transition-colors">
      <span
        aria-hidden
        className="inline-block h-2 w-2 shrink-0 rounded-sm border border-black/10"
        style={{ backgroundColor: swatch }}
      />
      <span className="font-semibold text-[#374151]">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        title={`Remove ${label}`}
        className="flex h-4 w-4 items-center justify-center rounded-full bg-[#fee2e2] text-xs font-bold text-[#dc2626] transition-colors hover:bg-[#dc2626] hover:text-white cursor-pointer border-none"
      >
        &times;
      </button>
    </span>
  );
}
